"use client";

import type { LocationDeal } from "~/graphql-client/generated";
import { dealToProduct } from "~/hooks/deals";
import type {
  CartItem,
  CartItemDetailed,
  Price,
  Product,
  ProductCategory,
  ProductOption,
} from "~/types";

interface Result {
  cartDetails: CartItemDetailed[];
  cartTotalPrice: Price;
  itemCount: number;
  hasUnavailableItems: boolean;
  hasIncompleteDeal: boolean;
}

export interface CartItemValidation {
  valid: boolean;
  incompleteOptionIds: string[];
  incompleteChoices?: Record<string, CartItemValidation>;
}

// Validates that a cart item satisfies its product's option rules: every option
// with minQuantity > 0 has a matching selection, each option's selected quantity
// is within [minQuantity, maxQuantity], and nested choice products validate too.
// (Moved from ProductSection so the cart and checkout can reuse it.)
export function isValidCartItem(
  cartItem: CartItem,
  productOptions?: ProductOption[],
  isNestedProduct = false,
): CartItemValidation {
  const incompleteOptionIds: string[] = [];

  const incompleteChoices: Record<string, CartItemValidation> = {};
  if (productOptions) {
    for (const o of productOptions) {
      const cartItemOption = cartItem.options.find(
        (co) => co.optionId === o.id,
      );

      if (!cartItemOption && o.minQuantity > 0) {
        incompleteOptionIds.push(o.id);
      } else if (cartItemOption && !isValidChoice(cartItemOption.choices, o)) {
        incompleteOptionIds.push(o.id);
      }

      // Deal with recursion
      if (cartItemOption) {
        for (const c of cartItemOption.choices) {
          const subProduct = o.choices.find(
            (sc) => sc.product.id === c.productId,
          );
          if (subProduct) {
            const val = isValidCartItem(c, subProduct.product.options);
            if (!val.valid) {
              incompleteOptionIds.push(o.id);
              incompleteChoices[c.productId] = val;
            }
          }
        }
      }
    }
  }

  const rootValid = !isNestedProduct || cartItem.quantity > 0;

  const returnValue: CartItemValidation = {
    valid: !incompleteOptionIds.length && rootValid,
    incompleteOptionIds,
  };

  if (Object.keys(incompleteChoices).length > 0) {
    returnValue.incompleteChoices = incompleteChoices;
  }

  return returnValue;
}

function isValidChoice(choices: CartItem[], option: ProductOption) {
  const total = choices.reduce((total, c) => total + c.quantity, 0);

  // check max
  if (option.maxQuantity && total > option.maxQuantity) {
    return false;
  }
  // check min
  if (total < option.minQuantity) {
    return false;
  }

  return true;
}

// A deal cart item is "incomplete" when its configured options don't yet satisfy
// the deal's option rules. Only meaningful for deal items (isDeal).
export function isCartItemDealComplete(
  cartItem: CartItem,
  dealProduct?: Product,
): boolean {
  if (!dealProduct) return false;
  return isValidCartItem(cartItem, dealProduct.options).valid;
}

export function mapCartDetails(
  cartItems: CartItem[],
  menu: ProductCategory[],
  deals: LocationDeal[],
): Result {
  if (!menu.length) {
    return {
      cartDetails: [],
      cartTotalPrice: { amount: 0, taxIncluded: 0 },
      itemCount: 0,
      hasUnavailableItems: false,
      hasIncompleteDeal: false,
    };
  }

  const cartDetails = cartItems.map((item) => {
    if (item.isDeal) {
      const dealProduct = getDealAsProduct(deals, menu, item);
      return getCartItemDetails(item, dealProduct);
    }

    const product = getProduct(menu, item);
    return getCartItemDetails(item, product);
  });

  const cartTotalPrice = cartDetails.reduce(
    (totalPrice, item) => {
      if (!item.product) return totalPrice;
      const itemTotal = getCartItemTotal(item, item.product);
      totalPrice.amount = totalPrice.amount + itemTotal.amount;
      totalPrice.taxIncluded += itemTotal.taxIncluded ?? 0;
      return totalPrice;
    },
    { amount: 0, taxIncluded: 0 },
  );

  const hasUnavailableItems = cartDetails.some((item) => {
    return !isCartItemAvailable(item);
  });

  const hasIncompleteDeal = cartDetails.some(
    (item) => item.isDeal && item.isIncomplete,
  );

  // Exclude DealAsVoucher lines (deals with no options) from the count — a
  // voucher isn't a "product" the customer is buying.
  const itemCount = cartDetails.reduce((total, item) => {
    const isVoucher =
      item.isDeal &&
      Boolean(item.product) &&
      (item.product?.options?.length ?? 0) === 0;
    return isVoucher ? total : total + item.quantity;
  }, 0);

  return {
    cartDetails,
    cartTotalPrice,
    itemCount,
    hasUnavailableItems,
    hasIncompleteDeal,
  };
}

function getProduct(
  categories: ProductCategory[],
  cartItem: CartItem,
): Product | undefined {
  return categories
    .flatMap((cat) => cat.products || [])
    .find((product) => product.id === cartItem.productId);
}

function getDealAsProduct(
  deals: LocationDeal[],
  categories: ProductCategory[],
  cartItem: CartItem,
): Product | undefined {
  const deal = deals.find((d) => d.id === cartItem.productId);

  if (!deal) return undefined;

  const products = categories.flatMap((cat) => cat.products || []);

  return dealToProduct(deal, products);
}

export function getCartItemDetails(
  cartItem: CartItem,
  product?: Product,
): CartItemDetailed {
  const { lineItemId, productId, quantity, note } = cartItem;

  if (!product) {
    return {
      lineItemId,
      productId,
      quantity,
      hasUnavailableOptions: false,
      note,
      options: [],
      pooledFromPlain: cartItem.pooledFromPlain,
    };
  }

  let hasUnavailableOptions = false;

  const options = cartItem.options.map((o) => {
    const option = findMatchingOption(product.options || [], o.optionId);

    if (!option) {
      hasUnavailableOptions = true;
    }

    let hasUnavailableChoices = false;

    const choices = o.choices.map((c) => {
      const choiceProduct = option && findMatchingProduct(option, c.productId);

      if (!choiceProduct) {
        hasUnavailableChoices = true;
      }

      return getCartItemDetails(c, choiceProduct);
    });

    return {
      title: option?.title || "",
      optionId: o.optionId,
      choices,
      hasUnavailableChoices,
    };
  });

  return {
    lineItemId,
    productId,
    quantity,
    note,
    product,
    options,
    hasUnavailableOptions,
    isDeal: cartItem.isDeal,
    isIncomplete: cartItem.isDeal
      ? !isValidCartItem(cartItem, product.options).valid
      : undefined,
    pooledFromPlain: cartItem.pooledFromPlain,
  };
}

function findMatchingProduct(option: ProductOption, choiceProductId: string) {
  const choice = option.choices.find((c) => c.product.id === choiceProductId);

  return choice?.product;
}

function findMatchingOption(options: ProductOption[], optionId: string) {
  return options.find((o) => o.id === optionId);
}

interface Option {
  optionId: string;
  choices: Choice[];
}
interface Choice {
  productId: string;
  quantity: number;
  options: Option[];
}

export function getCartItemTotal(
  cartItem: {
    quantity: number;
    options: Option[];
  },
  product?: Product,
): Price {
  // loading
  if (!product) return { amount: 0, taxIncluded: 0 };

  let total = cartItem.quantity * product.price.amount;
  let totalTaxIncluded = cartItem.quantity * (product.price.taxIncluded ?? 0);

  const optionTotals = cartItem.options.reduce(
    (optionTotalPrice, o) => {
      o.choices.forEach((c) => {
        const option = findMatchingOption(product.options || [], o.optionId);
        const nestedProduct =
          option && findMatchingProduct(option, c.productId);
        if (nestedProduct) {
          const nestedPrice = getCartItemTotal(c, nestedProduct);
          optionTotalPrice.amount =
            optionTotalPrice.amount + nestedPrice.amount;
          optionTotalPrice.taxIncluded =
            optionTotalPrice.taxIncluded + (nestedPrice.taxIncluded ?? 0);
        }
      });

      return optionTotalPrice;
    },
    { amount: 0, taxIncluded: 0 },
  );

  total = total + optionTotals.amount * cartItem.quantity;
  totalTaxIncluded += optionTotals.taxIncluded * cartItem.quantity;

  return { amount: total, taxIncluded: totalTaxIncluded };
}

function isCartItemAvailable(cartItem: CartItemDetailed): boolean {
  if (!cartItem.product) return false;
  if (cartItem.product.available === false) return false;
  if (cartItem.hasUnavailableOptions) return false;

  let available = true;
  cartItem.options.forEach((o) => {
    o.choices.forEach((c) => {
      if (!isCartItemAvailable(c)) {
        available = false;
      }
    });
  });

  return available;
}
